/**
 * structureSilhouetteMeshes — Tier-readable Murder Bridge structure silhouettes (AR-R31).
 */
import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  OctahedronGeometry,
  RingGeometry,
  TorusGeometry
} from 'three';

export const STRUCTURE_SILHOUETTE_TIERS = Object.freeze([
  'outer',
  'inhibitorTurret',
  'nexusTurret',
  'inhibitor',
  'nexus'
]);

/** Loop 22 readability residual (FID-MAP-09 / SPY-2002). */
export const STRUCTURE_SILHOUETTE_READABILITY = Object.freeze({
  outerGemSize: 1.09,
  inhibitorTurretGemSize: 0.89
});

const TURRET_PROFILES = Object.freeze({
  outer: Object.freeze({
    radiusTop: 1.05, radiusBot: 1.45, height: 5.2, gemY: 5.9,
    gemSize: STRUCTURE_SILHOUETTE_READABILITY.outerGemSize
  }),
  inhibitorTurret: Object.freeze({
    radiusTop: 1.35, radiusBot: 1.85, height: 4.2, gemY: 4.9,
    gemSize: STRUCTURE_SILHOUETTE_READABILITY.inhibitorTurretGemSize
  }),
  nexusTurret: Object.freeze({
    radiusTop: 1.15, radiusBot: 1.55, height: 3.2, gemY: 3.85, gemSize: 0.65
  })
});

function tag(mesh, part, tier, baseColor) {
  mesh.userData.structurePart = part;
  mesh.userData.structureTier = tier;
  if (baseColor !== undefined) mesh.userData.baseColor = baseColor;
  return mesh;
}

function addRangeRing(group, color, tier) {
  const ring = new Mesh(
    new RingGeometry(7.7, 7.8, 64).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({ color, side: DoubleSide, transparent: true, opacity: 0.3 })
  );
  ring.position.y = 0.03;
  group.add(tag(ring, 'range', tier));
}

function addTurretTierMeshes(group, tier, color) {
  const profile = TURRET_PROFILES[tier] || TURRET_PROFILES.outer;
  const baseMat = new MeshStandardMaterial({ color: 0x334155, roughness: 0.3 });
  const gemMat = new MeshStandardMaterial({ color, emissive: color, roughness: 0.1 });
  const pillar = new Mesh(
    new CylinderGeometry(profile.radiusTop, profile.radiusBot, profile.height, 12),
    baseMat
  );
  pillar.position.y = profile.height / 2;
  group.add(tag(pillar, 'base', tier, 0x334155));

  if (tier === 'inhibitorTurret') {
    const band = new Mesh(
      new CylinderGeometry(profile.radiusTop + 0.25, profile.radiusTop + 0.25, 0.45, 16),
      new MeshStandardMaterial({ color: 0x1e293b, metalness: 0.4, roughness: 0.35 })
    );
    band.position.y = profile.height * 0.55;
    group.add(tag(band, 'base', tier, 0x1e293b));
  }

  if (tier === 'nexusTurret') {
    const crown = new Mesh(
      new ConeGeometry(0.55, 0.9, 5),
      new MeshStandardMaterial({ color: 0x64748b, roughness: 0.4 })
    );
    crown.position.y = profile.gemY + 0.55;
    group.add(tag(crown, 'base', tier, 0x64748b));
  }

  const gem = new Mesh(new OctahedronGeometry(profile.gemSize), gemMat);
  gem.position.y = profile.gemY;
  group.add(tag(gem, 'gem', tier, color));
  addRangeRing(group, color, tier);
}

function addInhibitorMeshes(group, tier, color) {
  const baseMat = new MeshStandardMaterial({ color: 0x1e293b, roughness: 0.45 });
  const crystalMat = new MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.55, roughness: 0.2
  });
  const plinth = new Mesh(new BoxGeometry(3.2, 1.0, 3.2), baseMat);
  plinth.position.y = 0.5;
  group.add(tag(plinth, 'base', tier, 0x1e293b));
  const postL = new Mesh(new BoxGeometry(0.35, 2.4, 0.35), baseMat);
  postL.position.set(-1.35, 1.7, 0);
  group.add(tag(postL, 'base', tier, 0x1e293b));
  const postR = new Mesh(new BoxGeometry(0.35, 2.4, 0.35), baseMat);
  postR.position.set(1.35, 1.7, 0);
  group.add(tag(postR, 'base', tier, 0x1e293b));
  const crystal = new Mesh(new OctahedronGeometry(1.6, 0), crystalMat);
  crystal.position.y = 2.4;
  group.add(tag(crystal, 'crystal', tier, color));
}

function addNexusMeshes(group, tier, color) {
  const baseMat = new MeshStandardMaterial({ color: 0x0f172a, roughness: 0.5 });
  const coreMat = new MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.85, roughness: 0.15
  });
  const pad = new Mesh(new CylinderGeometry(3.4, 3.8, 0.6, 24), baseMat);
  pad.position.y = 0.3;
  group.add(tag(pad, 'base', tier, 0x0f172a));
  const core = new Mesh(new IcosahedronGeometry(2.2, 1), coreMat);
  core.position.y = 2.6;
  group.add(tag(core, 'core', tier, color));
  const halo = new Mesh(
    new TorusGeometry(2.8, 0.08, 8, 32),
    new MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.4 })
  );
  halo.rotation.x = Math.PI / 2;
  halo.position.y = 2.6;
  group.add(tag(halo, 'core', tier, color));
}

/**
 * @param {import('three').Group} group
 * @param {{ type?: string, tier?: string }} entity
 * @param {number} color
 */
export function addStructureSilhouetteMeshes(group, entity = {}, color = 0x3b82f6) {
  const type = entity.type || 'turret';
  const tier = entity.tier
    || (type === 'inhibitor' ? 'inhibitor' : type === 'nexus' ? 'nexus' : 'outer');
  if (type === 'nexus' || tier === 'nexus') {
    addNexusMeshes(group, 'nexus', color);
    return;
  }
  if (type === 'inhibitor' || tier === 'inhibitor') {
    addInhibitorMeshes(group, 'inhibitor', color);
    return;
  }
  const turretTier = TURRET_PROFILES[tier] ? tier : 'outer';
  addTurretTierMeshes(group, turretTier, color);
}
